import { memo, useEffect, useState } from 'react';
import { LocationTrackingDisplayData, TrackingActivePosition, TrackingBannerConfig, TrackingCardState } from './types';
import { ETADisplay } from './components/ETADisplay/ETADisplay';
import { OCSTimeDisplay } from './components/OCSTimeDisplay/OCSTimeDisplay';
import { TransportIcon } from './components/TransportIcon/TransportIcon';
import { StatusBanner } from './components/StatusBanner/StatusBanner';
import { RouteProgressRow } from './components/RouteProgressRow/RouteProgressRow';
import { LastKnownLocationDisplay } from './components/LastKnownLocationDisplay/LastKnownLocationDisplay';
import { formatTimeRemaining } from './display.utils';
import { useIsLastKnownPosition } from '@/hooks/useIsLastKnownPosition';
import { ORGAN_ICON } from '@/utils/helpers';
import { OrganType } from '@/api/apis/case';

const BANNER_CONFIGS: Record<Exclude<TrackingCardState, TrackingCardState.InTransit>, TrackingBannerConfig> = {
  [TrackingCardState.Default]: {
    message: 'Location tracking will be available once the team leaves the donor hospital.',
    showArrivalTime: false,
    progress: 0,
    progressVariant: 'static',
  },
  [TrackingCardState.AtTxCenter]: {
    message: 'TMDX team has arrived at TX Center with the organ.',
    showArrivalTime: true,
    progress: 100,
    progressVariant: 'completed',
  },
  [TrackingCardState.Unavailable]: {
    message: 'Location tracking is currently unavailable. Please contact TMDX for more details.',
    showArrivalTime: false,
    progress: 0,
    progressVariant: 'static',
  },
  [TrackingCardState.NonTmdxLogistics]: {
    message: 'Location tracking is currently unavailable. TMDX is not a logistics provider on this case.',
    showArrivalTime: false,
    progress: 0,
    progressVariant: 'static',
  },
};

const COUNTDOWN_TICK_MS = 60_000;

const ORGAN_ICON_STYLE = {
  color: '#e7bcc5',
} as const;

const computeRemaining = (timestamp: string | null) => (timestamp ? formatTimeRemaining(timestamp) : null);

const useTimeRemaining = (etaTimestamp: string | null): string | null => {
  const [remaining, setRemaining] = useState(() => computeRemaining(etaTimestamp));

  useEffect(() => {
    const update = () => {
      setRemaining(computeRemaining(etaTimestamp));
    };

    update();

    if (!etaTimestamp) {
      return undefined;
    }

    const intervalId = window.setInterval(update, COUNTDOWN_TICK_MS);

    return () => window.clearInterval(intervalId);
  }, [etaTimestamp]);

  return remaining;
};

type TrackingStatusCardProps = {
  cardState: TrackingCardState;
  title: string;
  data: LocationTrackingDisplayData;
  organType: OrganType;
  routeProgress?: number | null;
  isGroundLeg?: boolean;
  activePosition?: TrackingActivePosition | null;
};

const TrackingStatusCardComponent = ({
  cardState,
  title,
  data,
  organType,
  routeProgress = null,
  isGroundLeg = false,
  activePosition = null,
}: TrackingStatusCardProps) => {
  const banner = cardState !== TrackingCardState.InTransit ? BANNER_CONFIGS[cardState] : null;

  const expectedArrival = useTimeRemaining(data.etaTimestamp);
  const OrganIcon = ORGAN_ICON[organType];

  const isLastKnownPosition = useIsLastKnownPosition(
    activePosition?.timestamp,
    cardState === TrackingCardState.InTransit,
  );

  const shouldShowLastKnownPosition =
    cardState === TrackingCardState.InTransit && activePosition !== null && isLastKnownPosition;

  return (
    <div className="flex h-[172px] w-[640px] flex-col justify-between rounded-lg bg-base-70 p-6 pt-4 shadow-lg">
      <div className="flex items-center">
        <div className="mr-2 flex h-7 w-7 items-center justify-center rounded-full bg-maroon-20">
          <OrganIcon className="h-5 w-5" style={ORGAN_ICON_STYLE} />
        </div>

        <h3 className="flex-1 text-base-10">{title}</h3>
      </div>
      {banner ? (
        <div className="flex flex-1 flex-col">
          <StatusBanner
            message={banner.message}
            displayDate={banner.showArrivalTime ? (data.etaDate ?? undefined) : undefined}
            displayTime={banner.showArrivalTime ? (data.etaTime ?? undefined) : undefined}
            displayTz={banner.showArrivalTime ? (data.etaTz ?? undefined) : undefined}
          />
          <RouteProgressRow
            donorHospitalCode={data.donorHospitalCode}
            txCenterCode={data.txCenterCode}
            progress={banner.progress}
            variant={banner.progressVariant}
          />
        </div>
      ) : (
        <div className="flex flex-1 flex-col">
          <div className="flex flex-1 items-center gap-6">
            <OCSTimeDisplay organOnOCSTimestamp={data.organOnOCSTimestamp} caseName={data.caseName} />
            <TransportIcon isGround={isGroundLeg} />
            <ETADisplay
              etaDate={data.etaDate}
              etaTime={data.etaTime}
              etaTz={data.etaTz}
              arrivalLabel={data.arrivalLabel}
              txCenterOrTime={data.txCenterOrTime}
              txCenterOrTz={data.txCenterOrTz}
            />
          </div>
          {routeProgress !== null ? (
            <div className="mt-[-4px]">
              <RouteProgressRow
                donorHospitalCode={data.donorHospitalCode}
                txCenterCode={data.txCenterCode}
                progress={routeProgress * 100}
                variant="static"
              />
            </div>
          ) : null}

          {shouldShowLastKnownPosition && activePosition ? (
            <LastKnownLocationDisplay timestamp={activePosition.timestamp} />
          ) : null}

          {expectedArrival ? (
            <div className="mt-2 flex items-center self-center rounded-full bg-base-90 p-1">
              <div className="mr-2 rounded-full bg-success-70 px-2 py-1 text-xs-sb uppercase">Live</div>

              <div className="text-s-r text-base-5">Expect in {expectedArrival}</div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export const TrackingStatusCard = memo(TrackingStatusCardComponent);
